/* @jsx h */
import { defineComponent, h, computed } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type {
  CoinRedemptionTier,
  PromotionReward,
  CoinRequirement,
} from "@lemmonhq/types";
import {
  PromotionRewardEditor,
  defaultRewardForType,
} from "../../components/PromotionRewardEditor";
import { coinIconOf } from "./icons";

/** The coin cost of a tier, read back out of its requirement array. */
export const tierAmountOf = (tier: CoinRedemptionTier): number => {
  const coin = (tier.requirements ?? []).find(
    (r): r is CoinRequirement => r.type === "coin",
  );
  return coin?.amount ?? 0;
};

export type TierDraft = {
  /** Absent on a program that has not been created yet. */
  id?: string;
  amount: number;
  reward: PromotionReward;
};

export const emptyTierDraft = (groupOwned = false): TierDraft => ({
  amount: 10,
  reward: defaultRewardForType(groupOwned ? "order_discount" : "product"),
});

export const tierToDraft = (tier: CoinRedemptionTier): TierDraft => ({
  id: tier.id,
  amount: tierAmountOf(tier),
  reward: tier.reward,
});

export type RedemptionTiersEditorProps = {
  value: TierDraft;
  coinLabel: string;
  icon: string | null;
  onChange: (tier: TierDraft) => void;
  groupOwned?: boolean;
};

/**
 * The redemption side of a program: how many coins buy what.
 *
 * A tier is an ordinary promotion, so the reward half reuses
 * `PromotionRewardEditor` — the same control the promotions page uses. Only the
 * coin cost is specific to coins, and it is written back into the requirement
 * array server-side so the program linkage cannot drift.
 */
export const RedemptionTiersEditor =
  defineComponent<RedemptionTiersEditorProps>({
    name: "RedemptionTiersEditor",
    props: ["value", "coinLabel", "icon", "onChange", "groupOwned"],
    setup(props) {
      const { t } = useLocalization();

      const amount = computed(() => props.value.amount);

      const patch = (fields: Partial<TierDraft>) =>
        props.onChange({ ...props.value, ...fields });

      return () => (
        <div class="space-y-4">
          <p class="text-sm text-base-content/50">
            {t("promotions.coins.tier_description")}
          </p>

          <div class="rounded-xl border border-base-300 bg-base-100 p-4 space-y-4">
            <div class="form-control">
              <label class="label py-1">
                <span class="label-text font-medium">
                  {t("promotions.coins.tier_cost")}
                </span>
              </label>
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <span class="material-symbols-outlined text-lg text-primary">
                    {coinIconOf(props.icon)}
                  </span>
                </div>
                <input
                  type="number"
                  min={1}
                  value={amount.value}
                  onInput={(e) => {
                    const parsed = parseInt(
                      (e.target as HTMLInputElement).value,
                    );
                    patch({ amount: Number.isFinite(parsed) ? parsed : 0 });
                  }}
                  class="input input-bordered input-sm w-28 focus:input-primary"
                />
                <span class="text-sm text-base-content/60">
                  {props.coinLabel}
                </span>
              </div>
              <label class="label py-1">
                <span class="label-text-alt text-base-content/50">
                  {t("promotions.coins.tier_cost_hint", [
                    amount.value || 0,
                    props.coinLabel,
                  ])}
                </span>
              </label>
            </div>

            <div class="border-t border-base-300 pt-4">
              <PromotionRewardEditor
                value={props.value.reward}
                onChange={(reward: PromotionReward) => patch({ reward })}
                title={t("promotions.coins.tier_reward")}
                allowProductReward={true}
                allowedRewardTypes={
                  props.groupOwned ? ["order_discount"] : undefined
                }
              />
            </div>
          </div>
        </div>
      );
    },
  });
